import { useState } from 'react';
import {
  parsePresetAmounts,
  parseAmountDescriptions,
  parseFrequencies,
  parseDedicationOptions,
} from '../../shared/givingHelpers';
import useCustomFieldValues from '../../shared/hooks/useCustomFieldValues';
import { canSubmitCustomFieldValue } from '../../CustomFieldValues/actions/customFieldValuesActionCreators';
import { sharedAttributionVisitorId, useAttributionVisitor } from '../../../shared/attribution/useAttributionVisitor';
import { computeExpectedTotal, computeProcessingFee, MINIMUM_AMOUNT } from '../../../shared/donationAmount';

// 'details' → collect amount / donor info, 'payment' → confirm card via Stripe,
// 'thankYou' → confirmation. A Stripe redirect (3DS) returns to the page with
// redirect_status=succeeded, so we start in 'thankYou' in that case.
const initialPhase = () => {
  if (typeof window === 'undefined') return 'details';
  const params = new URLSearchParams(window.location.search);
  return params.get('redirect_status') === 'succeeded' ? 'thankYou' : 'details';
};

const csrfToken = () => {
  if (typeof document === 'undefined') return null;
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : null;
};

const useDonationFormState = ({
  presetAmounts,
  presetAmountDescriptions,
  enabledFrequencies,
  dedicationOptions,
  donationsCreateUrl,
  recurringDonationsCreateUrl,
  feeRules,
  turnstileRef,
  giftQuestions = [],
}) => {
  const attributionVisitorUuid = useAttributionVisitor('od_giving_embed.js');
  const amounts = parsePresetAmounts(presetAmounts);
  const amountDescriptions = parseAmountDescriptions(presetAmountDescriptions);
  const hasDescriptions = amounts.some((a) => amountDescriptions[a]);
  const frequencies = parseFrequencies(enabledFrequencies);
  const parsedDedicationOptions = parseDedicationOptions(dedicationOptions);

  const {
    customFieldValues: giftAnswers,
    handleCustomFieldChange: setGiftAnswer,
    resetCustomFieldValues: resetGiftAnswers,
  } = useCustomFieldValues(giftQuestions, []);
  const unansweredGiftQuestions = giftAnswers.filter((answer) => !canSubmitCustomFieldValue(answer));
  const giftAnswersComplete = unansweredGiftQuestions.length === 0;

  const [frequency, setFrequency] = useState(frequencies[0] || 'one_time');
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [isCustom, setIsCustom] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [dedicateGift, setDedicateGift] = useState(false);
  const [dedicationType, setDedicationType] = useState('');
  const [honoree, setHonoree] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [coverProcessing, setCoverProcessing] = useState(true);

  const [phase, setPhase] = useState(initialPhase);
  const [clientSecret, setClientSecret] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Counts failed Give clicks. 0 = donor hasn't tried yet, so no error styling;
  // the form body also uses each increment as the cue to focus the first bad field.
  const [validationAttempt, setValidationAttempt] = useState(0);

  const activeAmount = isCustom ? (parseFloat(customAmount) || 0) : selectedAmount;
  const processingFee = computeProcessingFee(activeAmount || 0, feeRules || []);
  const chargeTotal = computeExpectedTotal(activeAmount || 0, coverProcessing, feeRules || []);
  const isRecurring = frequency !== 'one_time';
  // Mirror Orders::Donation::MINIMUM_AMOUNT — below it the backend raises
  // InvalidAmountError, so gating here avoids a guaranteed 422 round-trip.
  // The email shape check is intentionally loose (x@y.z): the submit is a plain
  // button, not a native <form>, so the input's type="email" validation never
  // fires — without this a typo'd address gets bound to a live recurring pledge.
  const emailValid = /\S+@\S+\.\S+/.test(email.trim());
  const detailsComplete =
    activeAmount >= MINIMUM_AMOUNT && firstName.trim() && lastName.trim() && emailValid && giftAnswersComplete;

  // Recomputed every render, so a field's error clears as soon as the donor fixes it.
  const fieldErrors = {};
  if (validationAttempt > 0) {
    if (activeAmount < MINIMUM_AMOUNT) fieldErrors.amount = 'Please choose an amount';
    if (!firstName.trim()) fieldErrors.firstName = 'First name is required';
    if (!lastName.trim()) fieldErrors.lastName = 'Last name is required';
    if (!email.trim()) {
      fieldErrors.email = 'Email address is required';
    } else if (!emailValid) {
      fieldErrors.email = 'Enter a valid email address';
    }
    if (!giftAnswersComplete) fieldErrors.giftAnswers = `Please check: ${unansweredGiftQuestions.map((answer) => answer.custom_field.name).join(', ')}`;
  }

  const selectPreset = (amount) => {
    setSelectedAmount(amount);
    setIsCustom(false);
    setCustomAmount('');
  };

  const selectCustom = () => {
    setIsCustom(true);
    setSelectedAmount(null);
  };

  const submitDonation = async () => {
    if (submitting) return;
    if (!detailsComplete) {
      setValidationAttempt((n) => n + 1);
      return;
    }
    setValidationAttempt(0);
    setSubmitting(true);
    setError(null);

    const expectedTotal = computeExpectedTotal(activeAmount, coverProcessing, feeRules || []);
    const token = csrfToken();
    const createUrl = isRecurring ? recurringDonationsCreateUrl : donationsCreateUrl;

    // The server rejects token-less donations, so a missing widget is a hard
    // stop here. Turnstile tokens are single-use; executeAsync resets the
    // widget before each run, so a failed POST followed by another Give click
    // gets a fresh token.
    let recaptchaToken;
    try {
      if (!turnstileRef || !turnstileRef.current) {
        throw new Error('Turnstile widget not available');
      }

      recaptchaToken = await turnstileRef.current.executeAsync();

      if (!recaptchaToken) {
        throw new Error('Turnstile token missing');
      }
    } catch (e) {
      console.error('[DonationTurnstileFailure]', e);
      if (window.DD_RUM && typeof window.DD_RUM.addError === 'function') {
        window.DD_RUM.addError(e, { context: 'donation_turnstile' });
      }
      if (turnstileRef && turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setError('Unable to verify you are human. Please try again.');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch(createUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { 'X-CSRF-Token': token } : {}),
        },
        body: JSON.stringify({
          donation: {
            amount: activeAmount,
            first_name: firstName,
            last_name: lastName,
            email,
            cover_processing: coverProcessing,
            expected_total: expectedTotal,
            frequency,
            custom_field_values_attributes: giftAnswers
              .filter((answer) => String(answer.value ?? '').trim() !== '')
              .map((answer) => ({ custom_field_id: answer.custom_field_id, value: answer.value })),
          },
          recaptcha_token: recaptchaToken,
          attribution_visitor_uuid: attributionVisitorUuid || sharedAttributionVisitorId() || '',
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Rack::Attack's 429 body is {error, error_description}, not {errors},
        // so it needs its own donor-facing message.
        if (response.status === 429) {
          setError('Too many attempts. Please wait a minute and try again.');
        } else {
          setError((data.errors || ['Something went wrong. Please try again.']).join(' '));
        }
        setSubmitting(false);
        return;
      }

      if (data.paid) {
        setPhase('thankYou');
      } else {
        setClientSecret(data.stripe_client_secret);
        setPhase('payment');
      }
    } catch (e) {
      setError('Could not reach the server. Please try again.');
    }

    setSubmitting(false);
  };

  const onPaymentSuccess = () => setPhase('thankYou');

  const reset = () => {
    setPhase('details');
    setClientSecret(null);
    setSubmitting(false);
    setError(null);
    setValidationAttempt(0);
    setFrequency(frequencies[0] || 'one_time');
    setSelectedAmount(null);
    setIsCustom(false);
    setCustomAmount('');
    setDedicateGift(false);
    setDedicationType('');
    setHonoree('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setCoverProcessing(true);
    resetGiftAnswers([]);
  };

  return {
    amounts,
    amountDescriptions,
    hasDescriptions,
    frequencies,
    parsedDedicationOptions,
    frequency,
    setFrequency,
    selectedAmount,
    isCustom,
    customAmount,
    setCustomAmount,
    dedicateGift,
    setDedicateGift,
    dedicationType,
    setDedicationType,
    honoree,
    setHonoree,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    coverProcessing,
    setCoverProcessing,
    activeAmount,
    processingFee,
    chargeTotal,
    isRecurring,
    detailsComplete,
    giftAnswers,
    setGiftAnswer,
    fieldErrors,
    validationAttempt,
    selectPreset,
    selectCustom,
    phase,
    clientSecret,
    submitting,
    error,
    submitDonation,
    onPaymentSuccess,
    reset,
  };
};

export default useDonationFormState;
